import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SessionService } from "../../services/persistService";
import searchAddressByCep from "../../services/getAddressByCEP";
import { useMainContext } from "../../context";
import { MaskCEP } from "../../utils/masks";
import API from "../../api";
import InputLabel from "../inputLabel";
import maskMoney from "../../utils/maskMoney";
import getLatLong from "../../services/getLatLong";
import { toast } from "react-toastify";

const AddressForm = () => {
  const {
    address,
    setAddress,
    setRestaurantDeliveryPrice,
    restaurantDeliveryPrice,
  } = useMainContext();

  const latLongTimeoutRef = useRef(null);
  const [cep, setCep] = useState("");
  const [street, setStreet] = useState("");
  const [number, setNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [reference, setReference] = useState("");
  const [loadingCep, setLoadingCep] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [error, setError] = useState("");

  const fetchData = async () => {
    if (address) {
      setCep(address.cep);
      setStreet(address.street);
      setNumber(address.number);
      setComplement(address.complement);
      setNeighborhood(address.district);
      setCity(address.city);
      setState(address.state);
      setReference(address.reference);
      return;
    }

    setLoadingCep(true);

    try {
      const localCep = SessionService.getItem("cep");
      setCep(localCep);

      const newAddress = await searchAddressByCep(localCep);

      if (!newAddress?.success) {
        return console.log("CEP inválido");
      }

      setState(newAddress.data.state);
      setCity(newAddress.data.city);
      setNeighborhood(newAddress.data.district);
    } finally {
      setLoadingCep(false);
    }
  };

  const isMinimalAddressFilled = useMemo(() => {
    if (
      cep === "" ||
      street === "" ||
      number === "" ||
      neighborhood === "" ||
      city === "" ||
      state === ""
    ) {
      return false;
    }

    return true;
  }, [cep, street, number, neighborhood, city, state]);

  const handleCepChange = useCallback(async (text) => {
    text = text.replaceAll(/\D/g, "");

    setCep(text);

    if (text.length === 8) {
      setLoadingCep(true);
      try {
        const response = await searchAddressByCep(text);
        const address = response.data;
        setStreet("");
        setNumber("");
        setComplement(address?.complement);
        setNeighborhood(address?.district);
        setCity(address?.city);
        setState(address?.state);
        setReference("");
      } finally {
        setLoadingCep(false);
      }
    }
  }, []);

  const handleAddressChange = useCallback(async () => {
    if (
      address?.cep === cep &&
      address?.street === street &&
      address?.district_id === neighborhood?.id &&
      address?.city_id === city?.id &&
      address?.state_id === state?.id
    ) {
      return;
    }

    setLoadingAddress(true);

    return getLatLong(cep, street, city.name, neighborhood.name, state.name)
      .then(({ lat, long }) => {
        if (!lat || !long) {
          setError(
            "Endereço inválido. Não conseguimos encontrar a localização desse endereço.",
          );
        }

        setAddress({
          cep,
          street,
          complement,
          number,
          reference,
          lat,
          long,
          state_id: state.id,
          city_id: city.id,
          district_id: neighborhood.id,
          state: state,
          city: city,
          district: neighborhood,
        });
      })
      .finally(() => setLoadingAddress(false));
  }, [
    cep,
    street,
    number,
    complement,
    neighborhood,
    city,
    state,
    reference,
    address,
  ]);

  useEffect(() => {
    if (!neighborhood?.id) {
      setRestaurantDeliveryPrice(null);
      SessionService.addItem("restaurantServesAtAddress", false);
      return;
    }

    const localRestaurantId = SessionService.getItem("restaurantId");

    if (!localRestaurantId) return;

    setLoadingPrice(true);

    API.findRestaurantMenu(localRestaurantId)
      .then((response) => {
        const deliveryPrices = response.data.restaurant.delivery_prices;

        let deliveryPrice = [];

        deliveryPrice = deliveryPrices.filter(
          (dp) => dp?.district?.id === neighborhood?.id,
        );

        if (deliveryPrice.length === 0) {
          setRestaurantDeliveryPrice(null);
          setNeighborhood("");
          SessionService.addItem("restaurantServesAtAddress", false);

          return toast.error("O restaurante não entrega nesse bairro");
        }

        setRestaurantDeliveryPrice(deliveryPrice[0].delivery_price);
        SessionService.addItem("restaurantServesAtAddress", true);
      })
      .finally(() => setLoadingPrice(false));
  }, [neighborhood]);

  useEffect(() => {
    clearTimeout(latLongTimeoutRef.current);

    if (isMinimalAddressFilled) {
      latLongTimeoutRef.current = setTimeout(handleAddressChange, 500);
    }

    return () => clearTimeout(latLongTimeoutRef.current);
  }, [cep, street, number, complement, neighborhood, city, state, reference]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <InputLabel
        label="CEP"
        placeholder="00000-000"
        maxLength={9}
        value={MaskCEP(cep)}
        onChange={(event) => handleCepChange(event.target.value)}
      />

      <SearchAddressSelect
        setValue={(value) => {
          console.log(value);
          setState(value);
          setCity("");
          setNeighborhood("");
        }}
        value={state}
        type="states"
        disabled={loadingCep}
      />

      <SearchAddressSelect
        setValue={(value) => {
          setCity(value);
          setNeighborhood("");
        }}
        value={city}
        stateId={state?.id}
        type="cities"
        disabled={!state?.id || loadingCep}
      />

      <SearchAddressSelect
        setValue={setNeighborhood}
        value={neighborhood}
        cityId={city?.id}
        stateId={state?.id}
        type="districts"
        disabled={!city?.id || !state?.id || loadingCep}
      />

      <div className="md:col-span-2">
        <InputLabel
          label="Rua"
          placeholder="Nome da rua"
          value={street ?? ""}
          onChange={(event) => setStreet(event.target.value)}
        />

        {loadingAddress && (
          <p className="text-sm mt-1 animate-pulse">Buscando endereço...</p>
        )}
        {error && <p className="text-sm mt-1 text-red-500">{error}</p>}
      </div>

      <InputLabel
        label="Número"
        placeholder="123"
        maxLength={10}
        value={number ?? ""}
        onChange={(event) => setNumber(event.target.value)}
      />

      <InputLabel
        label="Complemento"
        placeholder="Apto, Bloco, etc (opcional)"
        maxLength={30}
        value={complement ?? ""}
        containerClass="md:col-span-2"
        onChange={(event) => setComplement(event.target.value)}
      />

      <InputLabel
        label="Referência"
        placeholder="Próximo a... (opcional)"
        value={reference ?? ""}
        maxLength={40}
        containerClass="md:col-span-3"
        onChange={(event) => setReference(event.target.value)}
      />

      {restaurantDeliveryPrice !== undefined &&
        restaurantDeliveryPrice !== null && (
          <div className="md:col-span-3">
            <p className="text-md mt-4">
              Taxa de entrega:
              <span className="font-bold text-lg ml-2">
                {loadingPrice
                  ? "Calculando..."
                  : restaurantDeliveryPrice === 0
                    ? "Grátis"
                    : maskMoney(restaurantDeliveryPrice ?? 0)}
              </span>
            </p>
          </div>
        )}
    </div>
  );
};

const SearchAddressSelect = ({
  type,
  value = "",
  setValue = () => {},
  cityId = undefined,
  stateId = undefined,
  disabled = false,
}) => {
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(value?.name ?? "");
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState("");

  const searchData = useCallback(
    async (search = "") => {
      try {
        if (type === "districts") {
          const result = await API.getDistrict(cityId ?? "", search);
          setData(result.data.rows);
        } else {
          const result = await API.getCityOrState(
            type,
            stateId ?? "",
            cityId ?? "",
            search,
          );
          setData(result.data);
        }
      } catch (e) {
        setError("Não foi possível carregar os dados");
        return;
      } finally {
        setLoading(false);
      }
    },
    [stateId, cityId, type],
  );

  const handleSubmit = useCallback(
    (data) => {
      setValue(data);
      setSearch(data.name);
      setIsSearching(false);
      setData([]);
    },
    [setValue, setSearch, setIsSearching, setData],
  );

  const mapInfo = {
    states: {
      label: "Estado",
    },
    cities: {
      label: "Cidade",
    },
    districts: {
      label: "Bairro",
    },
  };

  const renderItems = useCallback(() => {
    if (loading) {
      return <p className="p-4 text-md text-gray-500">Carregando...</p>;
    }

    if (error) {
      return <p className="p-4 text-md text-red-500">{error}</p>;
    }

    if (data && data.length > 0) {
      return data?.map((item) => (
        <button
          key={item.id}
          onClick={() => handleSubmit(item)}
          className="w-full text-left p-2 rounded hover:bg-gray-200 transition-colors"
        >
          {item.name}
        </button>
      ));
    }

    return (
      <p className="p-4 text-md text-gray-500">Nenhuma opção encontrada</p>
    );
  }, [data, loading, error, handleSubmit]);

  const onBlur = useCallback(() => {
    setIsSearching(false);
    setSearch(value?.name ?? "");
  }, [setIsSearching, setSearch, value]);

  const debouncedSearch = useCallback(() => {
    clearTimeout(timeoutRef.current);

    if (search.length >= 3 && value?.name !== search) {
      setIsSearching(true);
      setLoading(true);
      timeoutRef.current = setTimeout(() => searchData(search), 500);

      return () => clearTimeout(timeoutRef.current);
    } else {
      setData([]);
    }
  }, [search, searchData, value]);

  useEffect(() => {
    setSearch(value?.name ?? "");
  }, [value, type]);

  useEffect(() => debouncedSearch(), [search]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        onBlur();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [value]);

  return (
    <div ref={containerRef} className="relative">
      <InputLabel
        label={mapInfo[type].label}
        placeholder="Digite 3 letras para buscar..."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        disabled={disabled}
      >
        {isSearching && (
          <div className="w-full absolute bottom-0 translate-y-full z-10 bg-white border-2 rounded shadow-lg">
            {renderItems()}
          </div>
        )}
      </InputLabel>
    </div>
  );
};

export default AddressForm;
